import { useEffect, useId, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import './SettingsModal.css'

const DIALOG_CLASS =
  'fixed top-4 right-4 z-50 flex h-auto max-h-[calc(100dvh-2rem)] w-[356px] max-w-[calc(100vw-2rem)] animate-fade-in flex-col overflow-hidden rounded-modal border-[0.5px] border-line bg-overlay text-sm text-ink shadow-elevate-modal max-mobile:inset-x-0 max-mobile:top-auto max-mobile:right-auto max-mobile:bottom-0 max-mobile:left-auto max-mobile:w-full max-mobile:max-w-full max-mobile:max-h-[92dvh] max-mobile:rounded-t-modal max-mobile:border-b-0'

const ROW =
  'group flex w-full cursor-pointer flex-nowrap items-center gap-1.5 border-b-[0.5px] border-line px-3 py-2 text-sm/sm tracking-heading text-ink/85 transition-colors hover:bg-ink/2.5 hover:text-ink'

const ICON_WRAP =
  'flex size-[24px] shrink-0 items-center justify-center overflow-hidden rounded-card bg-fill transition-colors group-hover:bg-ink/9'

const ACTION_VALUE =
  'shrink-0 whitespace-nowrap text-meta underline-offset-[3px] transition-colors group-hover:font-medium group-hover:text-ink group-hover:underline group-hover:decoration-ink'

const PICKER_VALUE =
  'flex min-w-min shrink-0 items-center gap-1 whitespace-nowrap underline-offset-[3px] transition-colors group-hover:font-medium group-hover:text-ink group-hover:underline group-hover:decoration-ink/45 text-meta'

const LANGUAGES = [
  { id: 'en', label: 'English' },
  { id: 'zh', label: '普通话' },
  { id: 'ja', label: '日本語' },
  { id: 'tr', label: 'Türkçe' },
  { id: 'es', label: 'Español' },
  { id: 'ru', label: 'Русский' },
]

const FILL_SOUNDS = ['Neutral', 'Chime', 'Dark', 'Palm Muted']
const DATE_FORMATS = ['MM/DD/YYYY', 'DD/MM/YYYY']

function CloseIcon() {
  return (
    <span data-icon="true" className="flex size-4 shrink-0 items-center justify-center">
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256" fill="currentColor" className="size-full">
        <path d="M205.66,194.34a8,8,0,0,1-11.32,11.32L128,139.31,61.66,205.66a8,8,0,0,1-11.32-11.32L116.69,128,50.34,61.66A8,8,0,0,1,61.66,50.34L128,116.69l66.34-66.35a8,8,0,0,1,11.32,11.32L139.31,128Z" />
      </svg>
    </span>
  )
}

function CaretIcon({ open }) {
  return (
    <span
      data-icon="true"
      className="flex size-[10px] shrink-0 items-center justify-center transition-transform duration-200 ease-out"
      style={{ transform: open ? 'rotate(90deg)' : 'rotate(0deg)' }}
    >
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256" fill="currentColor" aria-hidden="true" className="size-full">
        <path d="M181.66,133.66l-80,80a8,8,0,0,1-11.32-11.32L164.69,128,90.34,53.66a8,8,0,0,1,11.32-11.32l80,80A8,8,0,0,1,181.66,133.66Z" />
      </svg>
    </span>
  )
}

function PickerCaret({ open }) {
  return (
    <span
      data-icon="true"
      className="flex size-[10px] shrink-0 items-center justify-center transition-transform duration-200 ease-out"
      style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}
    >
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256" fill="currentColor" aria-hidden="true" className="size-full">
        <path d="M213.66,101.66l-80,80a8,8,0,0,1-11.32,0l-80-80A8,8,0,0,1,53.66,90.34L128,164.69l74.34-74.35a8,8,0,0,1,11.32,11.32Z" />
      </svg>
    </span>
  )
}
function CheckIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" className="size-3.5 text-ink" aria-hidden="true">
      <path d="M3.5 8.2 6.4 11.1 12.5 4.8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function Toggle({ on, accent = 'up' }) {
  return (
    <span
      data-state={on ? 'on' : 'off'}
      className="relative inline-block h-[16px] w-[36px] shrink-0 overflow-hidden rounded-full shadow-[inset_0_var(--hl-y)_0_0_var(--hl-top),0px_0px_0px_0.5px_var(--ring-crisp)]"
    >
      <span
        className={`pointer-events-none absolute inset-0 rounded-full transition-[background-color] duration-150 ease-out ${
          on ? (accent === 'fun' ? 'bg-fun settings-toggle-fun' : 'bg-up-tint') : 'bg-ink/10'
        }`}
      />
      <span
        className="pointer-events-none absolute top-[2px] left-[2px] box-border h-[12px] w-[18px] rounded-[999px] bg-white shadow-(--shadow-toggle-thumb) transition-toggle-thumb duration-150 ease-out"
        style={{ transform: on ? 'translateX(14px)' : 'translateX(0px)' }}
      />
    </span>
  )
}

function IconBox({ children }) {
  return (
    <span className={ICON_WRAP}>
      <span className="flex size-full items-center justify-center text-ink" aria-hidden="true">
        {children}
      </span>
    </span>
  )
}

function SettingIcon({ kind, on }) {
  const svgClass = 'size-full'
  if (kind === 'set-size') {
    return (
      <svg viewBox="0 0 24 24" fill="none" className={svgClass}>
        <path opacity="0.4" d="M15.501 8.667h-7a1.833 1.833 0 0 0-1.833 1.833v3c0 1.013.82 1.833 1.833 1.833h7c1.013 0 1.834-.82 1.834-1.833v-3c0-1.013-.821-1.833-1.834-1.833Z" fill="currentColor" />
        {on ? (
          <path d="M14.503 16.333a.833.833 0 0 1-.834-.833V13h.5a.5.5 0 0 0 0-1h-.5V8.5a.833.833 0 0 1 .834-.833.5.5 0 0 0 0-1c-.527 0-1 .226-1.334.583a1.824 1.824 0 0 0-1.333-.583.5.5 0 0 0 0 1c.459 0 .833.374.833.833V12h-.5a.5.5 0 0 0 0 1h.5v2.5a.833.833 0 0 1-.833.833.5.5 0 0 0 0 1c.526 0 .999-.226 1.333-.583.334.357.807.583 1.334.583a.5.5 0 0 0 0-1Z" fill="currentColor" />
        ) : null}
      </svg>
    )
  }
  if (kind === 'orderbook-flash') {
    return (
      <svg viewBox="0 0 24 24" fill="none" className={svgClass}>
        <rect x="6" y="6" width="12" height="5" rx="1" fill="#FF384F" />
        <rect x="6" y="13" width="12" height="5" rx="1" fill="#32D695" />
      </svg>
    )
  }
  if (kind === 'ask-bid-ratio') {
    return (
      <svg viewBox="0 0 24 24" fill="none" className={svgClass}>
        <rect x="6" y="9.5" width="8" height="5" rx="1" fill="#32D695" />
        <rect x="15" y="9.5" width="3" height="5" rx="1" fill="#FF384F" />
      </svg>
    )
  }
  if (kind === 'notification-sound') {
    return (
      <svg viewBox="0 0 24 24" fill="none" className={svgClass}>
        <path opacity="0.4" d="M12.717 7.074A.833.833 0 0 1 14 7.776v8.448a.833.833 0 0 1-1.283.702l-3.53-2.259H7.5A1.5 1.5 0 0 1 6 13.167v-2.334a1.5 1.5 0 0 1 1.5-1.5h1.687l3.53-2.259Z" fill="currentColor" />
        <path d="M15.296 10.782a.5.5 0 0 1 .561.2 1.84 1.84 0 0 1 0 2.036.5.5 0 0 1-.916-.278v-1.48a.5.5 0 0 1 .355-.478Z" fill="currentColor" />
        <path d="M16.221 9.407a.5.5 0 0 1 .707 0 3.667 3.667 0 0 1 0 5.186.5.5 0 0 1-.707-.707 2.667 2.667 0 0 0 0-3.772.5.5 0 0 1 0-.707Z" fill="currentColor" />
        {!on ? <path d="m17.525 7.153-9.346 9.707" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" /> : null}
      </svg>
    )
  }
  if (kind === 'fun-mode') {
    if (on) {
      return (
        <svg viewBox="0 0 24 24" fill="none" className={svgClass}>
          <path
            opacity="0.4"
            d="M8.502 16.667c-.304 0-.602-.12-.824-.342a1.166 1.166 0 0 1-.291-1.165l1.849-6.074a1.167 1.167 0 0 1 1.941-.486l4.226 4.226a1.167 1.167 0 0 1-.485 1.941l-6.075 1.849c-.113.034-.227.051-.341.051Z"
            fill="#BF29FF"
          />
          <path d="m10.472 16.121 2.826-.86-2.255-6.765a1.16 1.16 0 0 0-1.806.59l-.53 1.74 1.765 5.295Z" fill="#BF29FF" />
          <path
            d="m17.164 7.662-.631-.21-.21-.632c-.068-.204-.406-.204-.474 0l-.211.632-.631.21a.25.25 0 0 0 0 .474l.631.21.211.632a.25.25 0 0 0 .474 0l.21-.632.631-.21a.25.25 0 0 0 0-.474Z"
            fill="#BF29FF"
          />
          <circle cx="14.5" cy="9.5" r=".5" fill="#BF29FF" />
          <path
            d="M12.669 8.793a.5.5 0 0 1-.294-.905c.097-.07.276-.226.378-.481.133-.331.062-.637.024-.754a.5.5 0 0 1 .953-.304c.087.272.197.82-.049 1.432-.187.463-.495.754-.72.917a.497.497 0 0 1-.292.095Z"
            fill="#BF29FF"
          />
          <path
            d="M15.707 11.834a.497.497 0 0 1-.294-.096.5.5 0 0 1-.11-.698c.163-.225.455-.533.917-.718.608-.245 1.157-.137 1.431-.05a.5.5 0 1 1-.303.953c-.117-.037-.423-.109-.755.024-.24.097-.395.26-.481.379a.498.498 0 0 1-.405.206Z"
            fill="#BF29FF"
          />
        </svg>
      )
    }
    return (
      <svg viewBox="0 0 24 24" fill="none" className={svgClass}>
        <path
          opacity="0.4"
          d="M12 6.667A5.333 5.333 0 1 0 12 17.333 5.333 5.333 0 0 0 12 6.667Zm-2.333 6.167a1.667 1.667 0 1 1 0-3.334 1.667 1.667 0 0 1 0 3.334Zm4.667 0a1.667 1.667 0 1 1 0-3.334 1.667 1.667 0 0 1 0 3.334Z"
          fill="currentColor"
        />
        <path d="M12.835 14h-1.667a.5.5 0 0 0 0 1h1.667a.5.5 0 0 0 0-1Z" fill="currentColor" />
        <path
          d="M17.5 10.667h-1.064A2.168 2.168 0 0 0 14.333 9c-.891 0-1.657.542-1.99 1.313A1.251 1.251 0 0 0 12 10.265c-.118 0-.233.017-.343.048A2.168 2.168 0 0 0 9.667 9a2.168 2.168 0 0 0-2.103 1.667H6.5a.5.5 0 0 0 0 1h1.064a2.168 2.168 0 0 0 2.103 1.667 2.17 2.17 0 0 0 2.146-1.971c.097-.104.277-.104.374 0a2.17 2.17 0 0 0 2.146 1.971 2.168 2.168 0 0 0 2.103-1.667H17.5a.5.5 0 0 0 0-1Zm-7.833 1.667a1.168 1.168 0 0 1 0-2.334 1.168 1.168 0 0 1 0 2.334Zm4.666 0a1.168 1.168 0 0 1 0-2.334 1.168 1.168 0 0 1 0 2.334Z"
          fill="currentColor"
        />
      </svg>
    )
  }
  if (kind === 'deposit-method') {
    return (
      <svg viewBox="0 0 24 24" fill="none" className={svgClass}>
        <path opacity="0.4" d="M12 6.667A5.333 5.333 0 0 0 7.686 15.127a.5.5 0 0 0 .405.206h7.82a.5.5 0 0 0 .405-.206A5.333 5.333 0 0 0 12 6.667Z" fill="currentColor" />
        <path d="M16.835 17.333h-9.667a.5.5 0 0 1 0-1h9.667a.5.5 0 0 1 0 1Z" fill="currentColor" />
        <text x="12" y="13.7" textAnchor="middle" fontFamily="JetBrains Mono, monospace" fontSize="3.5" fontWeight="700" fill="currentColor">
          FUN
        </text>
      </svg>
    )
  }
  if (kind === 'auth-signatures') {
    return (
      <svg viewBox="0 0 24 24" fill="none" className={svgClass}>
        <path d="M6.5 9.4c1.2-2.5 2.3 2.1 3.5 0s2.3 2.1 3.5 0 2.2 1.8 3.1.4c.4-.7 1.1-.8 1.9-.6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M6.5 15.4c1.2-2.5 2.3 2.1 3.5 0s2.3 2.1 3.5 0 2.2 1.8 3.1.4c.4-.7 1.1-.8 1.9-.6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    )
  }
  if (kind === 'edit-layout') {
    return (
      <svg viewBox="0 0 24 24" fill="none" className={svgClass}>
        <path opacity="0.4" d="M11.5 16.2603L12.1139 14.4804C12.1878 14.2659 12.3115 14.0686 12.4713 13.9088L14.5787 11.8014C14.9755 11.4049 15.5338 11.178 16.1109 11.178C16.5515 11.178 16.9797 11.3119 17.3345 11.5505V9.16659C17.3345 8.15405 16.5137 7.33325 15.5012 7.33325H8.5013C7.48884 7.33325 6.66797 8.15405 6.66797 9.16659V14.8333C6.66797 15.8458 7.48884 16.6666 8.5013 16.6666H11.422C11.4292 16.5297 11.4542 16.3929 11.5 16.2603Z" fill="currentColor" />
        <path d="M17.1595 12.7323L16.9359 12.5087C16.4958 12.068 15.7273 12.0676 15.2859 12.5084L13.1785 14.6158C13.1247 14.6695 13.0841 14.7346 13.0593 14.8062L12.4454 16.5868C12.3829 16.7678 12.4291 16.9683 12.5645 17.1034C12.6599 17.1988 12.7875 17.2498 12.9181 17.2498C12.9727 17.2498 13.0278 17.241 13.0811 17.2225L14.8617 16.6086C14.9333 16.5838 14.9985 16.5432 15.0521 16.4894L17.1595 14.382C17.3799 14.1616 17.5013 13.8687 17.5013 13.5572C17.5013 13.2453 17.3799 12.9524 17.1595 12.7323Z" fill="currentColor" />
      </svg>
    )
  }
  if (kind === 'reset-layout') {
    return (
      <svg viewBox="0 0 24 24" fill="none" className={svgClass}>
        <path opacity="0.4" d="M12 17a5.01 5.01 0 0 1-4.444-2.707.5.5 0 0 1 .889-.459A4.006 4.006 0 0 0 12 16a4 4 0 1 0-2.828-6.828.5.5 0 0 1-.707-.707A5 5 0 1 1 12 17Z" fill="currentColor" />
        <path d="M10.596 9.388 8.288 7.263a.5.5 0 0 0-.835.303l-.332 2.53a.5.5 0 0 0 .572.56l2.64-.406a.5.5 0 0 0 .263-.862Z" fill="currentColor" />
      </svg>
    )
  }
  if (kind === 'notification-placement') {
    return (
      <svg viewBox="0 0 24 24" fill="none" className={svgClass}>
        <rect x="7" y="7" width="10" height="10" rx="3" fill="currentColor" fillOpacity="0.3" />
        <circle cx={on ? 16 : 8} cy="16" r="2.5" fill="white" stroke="#121218" />
      </svg>
    )
  }
  if (kind === 'footer') {
    return (
      <svg viewBox="0 0 24 24" fill="none" className={svgClass}>
        <rect x="6.668" y="7.333" width="10.667" height="7.833" rx="1.833" fill="currentColor" fillOpacity="0.4" />
        <rect x="6.668" y="12.667" width="10.667" height="4" rx="1.833" fill="currentColor" />
      </svg>
    )
  }
  if (kind === 'language') {
    return (
      <svg viewBox="0 0 24 24" fill="none" className={svgClass}>
        <path fillRule="evenodd" d="M10.667 7.5a.5.5 0 0 0-1 0v.833H7.5a.5.5 0 0 0 0 1h.876a4.783 4.783 0 0 0 1.038 2.316 5.03 5.03 0 0 1-.255.152 4.75 4.75 0 0 1-1.694.533.5.5 0 1 0 .069.998c.052-.004.104-.009.195-.021.115-.016.277-.043.471-.088a5.939 5.939 0 0 0 1.441-.545c.173-.095.347-.204.517-.328.357.268.756.486 1.188.643a.5.5 0 0 0 .34-.941 4.13 4.13 0 0 1-.774-.392 4.832 4.832 0 0 0 1.035-2.327h.886a.5.5 0 0 0 0-1h-2.166V7.5Z" fill="currentColor" fillOpacity="0.4" />
        <path fillRule="evenodd" d="M14.168 10.667a.5.5 0 0 0-.468.324l-2 5.334a.5.5 0 1 0 .936.351l.504-1.343h2.39l.503 1.343a.5.5 0 1 0 .936-.351l-2-5.334a.5.5 0 0 0-.468-.324h-.333Zm.987 3.666-.82-2.187-.82 2.187h1.64Z" fill="currentColor" />
      </svg>
    )
  }
  return (
    <svg viewBox="0 0 24 24" fill="none" className={svgClass}>
      <path opacity="0.4" fillRule="evenodd" d="M11.667 15.333a3.67 3.67 0 0 1 5.333-3.262V9.667H7v5.5C7 16.179 7.82 17 8.833 17h3.239a3.65 3.65 0 0 1-.405-1.667Z" fill="currentColor" />
      <path fillRule="evenodd" d="M10.333 6.833a.5.5 0 0 0-1 0v.834h-.5A1.833 1.833 0 0 0 7 9.5v.833h10V9.5a1.833 1.833 0 0 0-1.833-1.833h-.5v-.834a.5.5 0 0 0-1 0v.834h-3.334v-.834Z" fill="currentColor" />
    </svg>
  )
}

function ToggleRow({ label, kind, checked, onToggle }) {
  return (
    <button type="button" className={ROW} aria-pressed={checked} onClick={onToggle}>
      <IconBox>
        <SettingIcon kind={kind} on={checked} />
      </IconBox>
      <span className="settings-row-label">{label}</span>
      <span className="shrink-0" aria-hidden="true">
        <Toggle on={checked} />
      </span>
    </button>
  )
}

function PlayIcon() {
  return (
    <svg viewBox="0 0 10 10" fill="currentColor" className="size-2.5 shrink-0 text-ink" aria-hidden="true">
      <path d="M2.2 1.15a.6.6 0 0 0-.9.52v6.66a.6.6 0 0 0 .9.52l5.7-3.33a.6.6 0 0 0 0-1.04L2.2 1.15Z" />
    </svg>
  )
}

function SoundFillRow({ enabled, sound, pickerOpen, onToggle, onTogglePicker, onSelectSound }) {
  return (
    <div data-state={pickerOpen ? 'open' : 'closed'} className="border-b-[0.5px] border-line">
      <div className="group flex w-full items-center justify-between px-3 py-2 text-sm/sm tracking-heading text-ink/85 transition-colors hover:bg-ink/2.5 hover:text-ink">
        <button
          type="button"
          className="flex flex-1 cursor-pointer items-center gap-1.5 text-left"
          aria-pressed={enabled}
          onClick={onToggle}
        >
          <IconBox>
            <SettingIcon kind="notification-sound" on={enabled} />
          </IconBox>
          <span className="settings-row-label">Order Filled Sound</span>
        </button>
        <div className="flex items-center gap-2">
          {enabled ? (
            <button
              type="button"
              className="settings-sound-chip inline-flex min-w-min shrink-0 items-center gap-1 rounded-card px-1.5 py-0.5"
              aria-expanded={pickerOpen}
              data-state={pickerOpen ? 'open' : 'closed'}
              onClick={(event) => {
                event.stopPropagation()
                onTogglePicker()
              }}
            >
              <span className="settings-native-label" style={{ flexGrow: 0, height: 'auto' }}>
                {sound}
              </span>
              <PickerCaret open={pickerOpen} />
            </button>
          ) : null}
          <button type="button" className="shrink-0" aria-label="Order Filled Sound" onClick={onToggle}>
            <Toggle on={enabled} />
          </button>
        </div>
      </div>
      {enabled && pickerOpen ? (
        <div className="settings-picker-menu settings-sound-picker mx-3 mb-2 overflow-hidden rounded-card">
          {FILL_SOUNDS.map((item) => {
            const selected = sound === item
            return (
              <button
                key={item}
                type="button"
                aria-pressed={selected}
                className={`settings-picker-option flex w-full items-center justify-between gap-2 px-2.5 py-1.5 text-left text-sm/sm transition-colors hover:bg-ink/5 ${
                  selected ? 'font-medium' : ''
                }`}
                onClick={() => onSelectSound(item)}
              >
                <span className="flex min-w-0 items-center gap-2">
                  <PlayIcon />
                  <span className="settings-native-label">{item}</span>
                </span>
                {selected ? <CheckIcon /> : null}
              </button>
            )
          })}
        </div>
      ) : null}
    </div>
  )
}

function FunModeRow({ checked, onToggle }) {
  return (
    <button
      type="button"
      className={`settings-fun-row group flex w-full cursor-pointer flex-nowrap items-center gap-1.5 border-b-[0.5px] border-line px-3 py-2 text-sm/sm tracking-heading text-ink/85 transition-colors ${
        checked ? 'bg-fun-row' : 'hover:bg-ink/2.5 hover:text-ink'
      }`}
      data-on={checked ? 'true' : 'false'}
      aria-pressed={checked}
      onClick={onToggle}
    >
      <span
        className={`settings-fun-icon flex size-[24px] shrink-0 items-center justify-center overflow-hidden rounded-card transition-colors ${
          checked ? 'bg-fun/10' : 'bg-fill group-hover:bg-ink/9'
        }`}
      >
        <span className="flex size-full items-center justify-center text-ink" aria-hidden="true">
          <SettingIcon kind="fun-mode" on={checked} />
        </span>
      </span>
      <span className="settings-row-label">Fun Mode</span>
      <span className="shrink-0" aria-hidden="true">
        <Toggle on={checked} accent="fun" />
      </span>
    </button>
  )
}

function ActionRow({ label, kind, value, onClick }) {
  return (
    <button type="button" className={ROW} onClick={onClick}>
      <IconBox>
        <SettingIcon kind={kind} on />
      </IconBox>
      <span className="settings-row-label">{label}</span>
      <span className={ACTION_VALUE}>{value}</span>
    </button>
  )
}

function SettingsSection({ id, title, open, onToggle, children }) {
  const headingId = `settings-section-heading-${id}`
  const contentId = useId()
  return (
    <section role="group" aria-labelledby={headingId} data-settings-section={id} className={open ? '' : 'mb-2.5'}>
      <div data-state={open ? 'open' : 'closed'} className="flex min-h-0 w-full flex-col">
        <button
          type="button"
          aria-controls={contentId}
          aria-expanded={open}
          data-state={open ? 'open' : 'closed'}
          className="settings-section-trigger flex w-full cursor-pointer items-center justify-between px-[18px] py-1 text-left transition-colors"
          onClick={onToggle}
        >
          <span id={headingId} className="settings-section-heading">
            {title}
          </span>
          <CaretIcon open={open} />
        </button>
        <div
          data-state={open ? 'open' : 'closed'}
          id={contentId}
          hidden={!open}
          className="overflow-hidden data-[state=closed]:animate-collapsible-slide-up data-[state=open]:animate-collapsible-slide-down"
        >
          {open ? <div className="flex w-full flex-col pt-1 pb-5">{children}</div> : null}
        </div>
      </div>
    </section>
  )
}

function formatSampleDate(pattern) {
  const now = new Date()
  const m = now.getMonth() + 1
  const d = now.getDate()
  const y = now.getFullYear()
  const md = `${m}/${d}/${y}`
  const dm = `${d}/${m}/${y}`
  return pattern === 'DD/MM/YYYY' ? `${dm} - DD/MM/YYYY` : `${md} - MM/DD/YYYY`
}

export default function SettingsModal({ isOpen, onClose, settings, patchSettings, focusSection }) {
  const titleId = useId()
  const [openSections, setOpenSections] = useState(() => new Set(['orderbook', 'display']))
  const [langOpen, setLangOpen] = useState(false)
  const [dateOpen, setDateOpen] = useState(false)
  const [soundOpen, setSoundOpen] = useState(false)

  useEffect(() => {
    if (!isOpen) return
    if (focusSection === 'region') {
      setOpenSections((prev) => new Set([...prev, 'region']))
      setLangOpen(true)
      setDateOpen(false)
      requestAnimationFrame(() => {
        document
          .querySelector('[data-settings-section="region"]')
          ?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
      })
    }
  }, [isOpen, focusSection])

  useEffect(() => {
    if (!isOpen) return undefined
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKey = (event) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = previous
      window.removeEventListener('keydown', onKey)
    }
  }, [isOpen, onClose])

  const language = settings.language || 'en'
  const dateFormat = settings.dateFormat || 'MM/DD/YYYY'
  const placement = settings.notificationPlacement || 'bottom-right'
  const languageLabel = LANGUAGES.find((item) => item.id === language)?.label || 'English'

  const toggleSection = (id) => {
    setOpenSections((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const dateOptions = useMemo(
    () => DATE_FORMATS.map((pattern) => ({ pattern, label: formatSampleDate(pattern) })),
    [],
  )

  if (!isOpen) return null

  const open = (id) => openSections.has(id)

  return createPortal(
    <>
      <div
        data-state="open"
        className="fixed inset-0 z-50 animate-fade-in bg-scrim transition-all"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-labelledby={titleId}
        data-state="open"
        data-settings-root="true"
        data-header-menu="true"
        className={DIALOG_CLASS}
      >
        <div data-testid="settings-panel" className="flex min-h-0 w-full flex-col overflow-y-auto">
          <div className="mb-2 flex shrink-0 items-center justify-between gap-2 border-b-[0.5px] border-line p-3">
            <span id={titleId} className="min-w-min shrink-0 text-md/lg font-medium whitespace-nowrap text-ink">
              Settings
            </span>
            <button
              type="button"
              className="inline-flex shrink-0 items-center gap-2 p-0 text-sm/sm font-medium text-ink/50 no-underline transition-colors hover:text-ink active:text-ink disabled:opacity-50 disabled:no-underline"
              aria-label="Close"
              onClick={onClose}
            >
              <CloseIcon />
            </button>
          </div>

          <SettingsSection id="orderbook" title="Order Book" open={open('orderbook')} onToggle={() => toggleSection('orderbook')}>
            <ToggleRow
              label="Order Book Set Size on Click"
              kind="set-size"
              checked={!!settings.setSizeOnClick}
              onToggle={() => patchSettings({ setSizeOnClick: !settings.setSizeOnClick })}
            />
            <ToggleRow
              label="Orderbook Animation"
              kind="orderbook-flash"
              checked={!!settings.orderbookAnimation}
              onToggle={() => patchSettings({ orderbookAnimation: !settings.orderbookAnimation })}
            />
            <ToggleRow
              label="Ask/bid ratio"
              kind="ask-bid-ratio"
              checked={!!settings.askBidRatio}
              onToggle={() => patchSettings({ askBidRatio: !settings.askBidRatio })}
            />
          </SettingsSection>

          <SettingsSection id="display" title="Display" open={open('display')} onToggle={() => toggleSection('display')}>
            <ToggleRow
              label="Market News"
              kind="notification-sound"
              checked={!!settings.marketNews}
              onToggle={() => patchSettings({ marketNews: !settings.marketNews })}
            />
          </SettingsSection>

          <SettingsSection id="sounds" title="Sounds" open={open('sounds')} onToggle={() => toggleSection('sounds')}>
            <SoundFillRow
              enabled={!!settings.orderFilledSound}
              sound={settings.fillSound || 'Neutral'}
              pickerOpen={soundOpen}
              onToggle={() => {
                const next = !settings.orderFilledSound
                patchSettings({ orderFilledSound: next })
                if (!next) setSoundOpen(false)
              }}
              onTogglePicker={() => setSoundOpen((v) => !v)}
              onSelectSound={(value) => {
                patchSettings({ fillSound: value })
              }}
            />
            <ToggleRow
              label="Background Fill Notification"
              kind="notification-sound"
              checked={!!settings.backgroundFillNotification}
              onToggle={() => patchSettings({ backgroundFillNotification: !settings.backgroundFillNotification })}
            />
            <FunModeRow
              checked={!!settings.funMode}
              onToggle={() => patchSettings({ funMode: !settings.funMode })}
            />
          </SettingsSection>

          <SettingsSection id="deposit" title="Deposit" open={open('deposit')} onToggle={() => toggleSection('deposit')}>
            <ToggleRow
              label="Use Alternative Deposit Flow"
              kind="deposit-method"
              checked={!!settings.altDepositFlow}
              onToggle={() => patchSettings({ altDepositFlow: !settings.altDepositFlow })}
            />
          </SettingsSection>

          <SettingsSection id="security" title="Security" open={open('security')} onToggle={() => toggleSection('security')}>
            <ActionRow label="Auth signatures" kind="auth-signatures" value="2 signatures" />
          </SettingsSection>

          <SettingsSection id="layout" title="Layout" open={open('layout')} onToggle={() => toggleSection('layout')}>
            <ActionRow
              label="Edit Layout Mode"
              kind="edit-layout"
              value="Edit"
              onClick={() => window.dispatchEvent(new CustomEvent('lighter:edit-layout'))}
            />
            <ActionRow
              label="Reset Layout"
              kind="reset-layout"
              value="Reset"
              onClick={() => window.dispatchEvent(new CustomEvent('lighter:reset-layout'))}
            />
            <div className="group flex w-full flex-nowrap items-center gap-1.5 border-b-[0.5px] border-line px-3 py-2 text-sm/sm tracking-heading text-ink/85 transition-colors hover:bg-ink/2.5">
              <IconBox>
                <SettingIcon kind="notification-placement" on={placement === 'bottom-right'} />
              </IconBox>
              <span className="settings-row-label">Notification Placement</span>
              <span className="flex shrink-0 items-center gap-2" role="group">
                <button
                  type="button"
                  className={`settings-placement-btn border-none bg-transparent p-0 text-sm/sm tracking-heading whitespace-nowrap transition-colors ${
                    placement === 'bottom-left' ? 'cursor-default font-medium' : 'cursor-pointer font-normal'
                  }`}
                  aria-pressed={placement === 'bottom-left'}
                  onClick={() => patchSettings({ notificationPlacement: 'bottom-left' })}
                >
                  Bottom Left
                </button>
                <span className="h-[14px] w-px shrink-0 bg-ink/18" aria-hidden="true" />
                <button
                  type="button"
                  className={`settings-placement-btn border-none bg-transparent p-0 text-sm/sm tracking-heading whitespace-nowrap transition-colors ${
                    placement === 'bottom-right' ? 'cursor-default font-medium' : 'cursor-pointer font-normal'
                  }`}
                  aria-pressed={placement === 'bottom-right'}
                  onClick={() => patchSettings({ notificationPlacement: 'bottom-right' })}
                >
                  Bottom Right
                </button>
              </span>
            </div>
            <ToggleRow
              label="Show Footer"
              kind="footer"
              checked={settings.showFooter !== false}
              onToggle={() => patchSettings({ showFooter: settings.showFooter === false })}
            />
          </SettingsSection>

          <SettingsSection
            id="region"
            title="Language and Date Formatting"
            open={open('region')}
            onToggle={() => toggleSection('region')}
          >
            <div data-state={langOpen ? 'open' : 'closed'} className="border-b-[0.5px] border-line">
              <button
                type="button"
                data-testid="localization-picker-trigger-settings"
                className="group flex w-full cursor-pointer flex-nowrap items-center justify-between gap-1.5 px-3 py-2 text-sm/sm tracking-heading text-ink/85 transition-colors hover:bg-ink/2.5"
                aria-expanded={langOpen}
                data-state={langOpen ? 'open' : 'closed'}
                onClick={() => {
                  setLangOpen((v) => !v)
                  setDateOpen(false)
                }}
              >
                <span className="flex min-w-0 flex-1 items-center gap-1.5">
                  <IconBox>
                    <SettingIcon kind="language" />
                  </IconBox>
                  <span className="settings-row-label">Language</span>
                </span>
                <span className={`${PICKER_VALUE}${langOpen ? ' text-ink' : ''}`}>
                  <span>{languageLabel}</span>
                  <PickerCaret open={langOpen} />
                </span>
              </button>
              {langOpen ? (
                <div className="settings-picker-menu mx-3 mb-2 overflow-hidden rounded-card bg-transparent">
                  {LANGUAGES.map((item) => {
                    const selected = language === item.id
                    return (
                    <button
                      key={item.id}
                      type="button"
                      aria-pressed={selected}
                      className={`settings-picker-option flex w-full items-center justify-between px-2.5 py-1.5 text-left text-sm/sm transition-colors hover:bg-ink/5 ${
                        selected ? 'font-medium' : ''
                      }`}
                      onClick={() => {
                        patchSettings({ language: item.id })
                        setLangOpen(false)
                      }}
                    >
                      <span className="settings-native-label">{item.label}</span>
                      {selected ? <CheckIcon /> : null}
                    </button>
                    )
                  })}
                </div>
              ) : null}
            </div>
            <div data-state={dateOpen ? 'open' : 'closed'} className="border-b-[0.5px] border-line">
              <button
                type="button"
                className="group flex w-full cursor-pointer flex-nowrap items-center justify-between gap-1.5 px-3 py-2 text-sm/sm tracking-heading text-ink/85 transition-colors hover:bg-ink/2.5"
                aria-expanded={dateOpen}
                data-state={dateOpen ? 'open' : 'closed'}
                onClick={() => {
                  setDateOpen((v) => !v)
                  setLangOpen(false)
                }}
              >
                <span className="flex min-w-0 flex-1 items-center gap-1.5">
                  <IconBox>
                    <SettingIcon kind="date-formatting" />
                  </IconBox>
                  <span className="settings-row-label">Date Formatting</span>
                </span>
                <span className={`${PICKER_VALUE}${dateOpen ? ' text-ink' : ''}`}>
                  <span>{formatSampleDate(dateFormat)}</span>
                  <PickerCaret open={dateOpen} />
                </span>
              </button>
              {dateOpen ? (
                <div className="settings-picker-menu mx-3 mb-2 overflow-hidden rounded-card bg-transparent">
                  {dateOptions.map((item) => {
                    const selected = dateFormat === item.pattern
                    return (
                    <button
                      key={item.pattern}
                      type="button"
                      aria-pressed={selected}
                      className={`settings-picker-option flex w-full items-center justify-between px-2.5 py-1.5 text-left text-sm/sm transition-colors hover:bg-ink/5 ${
                        selected ? 'font-medium' : ''
                      }`}
                      onClick={() => {
                        patchSettings({ dateFormat: item.pattern })
                        setDateOpen(false)
                      }}
                    >
                      <span>{item.label}</span>
                      {selected ? <CheckIcon /> : null}
                    </button>
                    )
                  })}
                </div>
              ) : null}
            </div>
          </SettingsSection>
        </div>
      </div>
    </>,
    document.body,
  )
}
